<template>
  <romanza-default-section :section="section" :menu="menu" menu-item-component="wine-list-item"
                          class="menu--section-wine_list"></romanza-default-section>
</template>

<script lang="ts">
/**
 * Custom component for Romanza Wines by the Glass section.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.10.0
 * @copyright  Copyright (C) 2021-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2021-12-23 (1.0.0) (jhoward)
 *      - Special layout for "Wines by the Glass" section for Romanza menu.
 *
 *    2022-03-07 (1.0.1) (jhoward)
 *      - Loosen the regular expressions for bottle/glass price to be more forgiving.
 *
 *    2022-03-10 (1.1.0) (jhoward)
 *      - Refactor tags to use <menu-tag /> component.
 *      - Add <menu-tag-legend />.
 *
 *    2022-03-31 (1.2.0) (jhoward)
 *      - Rename Romanza_winesByTheGlass to Romanza_wineList.
 *      - Add menu version number to Romanza_wineList.vue.
 *      - Allow dynamic pricing columns in Romanza_wineList.vue.
 *      - Group items by new mi_item_group_name rather than mi_subtitle.
 *
 *    2022-04-05 (1.3.0) (jhoward)
 *      - Add sub-grouping for items.
 *      - Add support for wine BIN numbers.
 *
 *    2022-04-21 (1.4.0) (jhoward)
 *      - Update item grouping in Romanza_wineList.vue.
 *      - Smarter item pricing columns in Romanza_wineList.vue.
 *      - Compute this.items in Romanza_wineList.vue.
 *      - Add support for the following pricing types in Romanza_wineList.vue: Glass 30z, 750ml, 500ml, 375ml and default.
 *
 *    2022-10-04 (1.5.0) (jhoward)
 *      - Refactored most computed properties and methods in <Romanza_wineList> to a helper JS file to be included in other components as well.
 *
 *    2022-04-17 (1.6.0) (jhoward)
 *      - <Romanza_wineList> uses TypeScript.
 *      - <Romanza_wineList> section prop is required.
 *      - Remove unused methods from <Romanza_wineList>.
 *
 *    2022-06-19 (1.7.0) (jhoward)
 *      - <Romanza_wineList> supports section.ms_show_pricing_header.
 *
 *    2022-06-21 (1.7.1) (jhoward)
 *      - Default to 1 pricing type rather than 0 in <Romanza_wineList>.
 *
 *    2023-08-23 (1.8.0) (jhoward)
 *      - Use <menu-version> in <Romanza_wineList>.
 *
 *    2025-02-10 (1.9.0) (jhoward)
 *      - Add <menu-schedules> to the <Romanza_wineList> component if in admin view.
 *
 *    2025-04-08 (1.10.0) (jhoward)
 *      - Use <Romanza_defaultSection> in <Romanza_wineList>.
 *      - Remove "visual_" prefix from class names in <Romanza_wineList>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import RomanzaDefaultSection from "./Romanza_defaultSection.vue";

export default defineComponent({
  components: {
    RomanzaDefaultSection,
  },
  name: "Romanza_wineList",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
})
</script>

<style scoped>
.menu--section-wine_list:deep(.menu--section_subtitle) {
  margin-top: 1em;
  font-weight: 400;
  color: #000000;
}

.menu--section-romanza.menu--section-wine_list:deep(.menu--section_group_title) {
  font-size: 1.2em;
  margin-top: 1.2em;
}

.menu--section.menu--section-romanza.menu--section-wine_list:deep(.menu--section_subgroup_title) {
  margin-top: 0;
  text-align: left;
  position: relative;
  align-items: end;
  color: #000;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title span:first-child) {
  font-size: 1em;
}

.menu--section-wine_list.menu--section-show_pricing_header.menu--section-show_bin:deep(.menu--item_subgroup-show_BIN h3 span:nth-child(2)) {
  text-align: center;
}

.menu--section-wine_list:deep(.menu--item) {
  font-size: 0.9em;
}

.menu--section.menu--section-wine_list:deep(.menu--item_subgroup) {
  grid-gap: 0;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title span:first-child),
.menu--section-wine_list:deep(.menu--item_title){
  text-align: initial;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title span:not(:first-child)),
.menu--section-wine_list:deep(.menu--item_price){
  text-align: center;
  color: #000;
  font-weight: normal;
}

.menu--section-wine_list:deep(.menu--item_bin) {
  text-align: initial;
}
</style>